import React from 'react';
import { FormProvider } from 'react-hook-form';
import {
  AddBox as AddBoxIcon,
  ChevronRight as ChevronRightIcon,
  CloseRounded,
} from '@mui/icons-material';
import {
  Box,
  CircularProgress,
  Container,
  Divider,
  IconButton,
  List,
  ListItem,
  Pagination,
  Stack,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Button, Modal, Typo } from 'components/primitives';
import { PreLoaderSnippet } from 'components/snippets';
import { IncidentAddForm } from 'components/fragments';
import IncidentDetails from 'pages/Dashboard/IncidentDetails';
import useStyles from './IncidentsManage.styles';
import { IncidentsManageProps } from './IncidentsManage.props';
import useFetchIncident from './hooks/useFetchIncident';

const IncidentsManageView: React.FC<IncidentsManageProps> = ({
  incidentsList,
  handleAddModal,
  form,
  ...props
}) => {
  const classes = useStyles();
  const control = useFetchIncident();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <React.Fragment>
      <Container style={{ padding: '1px 24px 24px' }}>
        <Box className={classes.titleBox} sx={{ my: { xs: 1, md: 2 }, alignItems: 'center' }}>
          <Typo className={classes.titleBoxText} variant='h3'>
            Incidents
          </Typo>
          <Box className={classes.incidentsBadge}>
            <Typo variant='h4'>{incidentsList.length || 0}</Typo>
          </Box>
          <Divider />
          <Button
            startIcon={isMobile ? null : <AddBoxIcon />}
            sx={{ minWidth: isMobile ? 'unset' : '160px' }}
            onClick={() => handleAddModal(true)}>
            {isMobile ? <AddBoxIcon /> : 'New entry'}
          </Button>
          <CloseRounded
            style={{ cursor: 'pointer' }}
            onClick={() => props.handleManageIncidentModal(false)}
          />
        </Box>
        <PreLoaderSnippet
          visible={props.loading}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '300px',
          }}
        />
        {/* Incidents list */}
        <List hidden={props.loading} className={classes.listView} sx={{ mt: 2 }} disablePadding>
          {/* List Items Map */}
          {!props.loading
            ? incidentsList.length
              ? incidentsList?.map((incident, idx) => {
                  return (
                    <ListItem
                      key={idx}
                      onClick={() => props.onNavigate(`${incident.id}`)}
                      className={classes.listItem}
                      secondaryAction={
                        <IconButton
                          className={classes.iconButton}
                          onClick={() => props.onNavigate(`${incident.id}`)}>
                          <ChevronRightIcon />
                        </IconButton>
                      }
                      disablePadding>
                      <Box className={classes.leftColumn}>
                        <Typo variant='h5'>
                          <strong>{incident.name}</strong>
                        </Typo>
                        <Stack direction='row' columnGap={1} className={classes.meta}>
                          <Typo key={'Created on'} variant='body2' color='GrayText'>
                            {'Created on'}
                            <strong>{incident.date || ''}</strong>
                          </Typo>
                        </Stack>
                      </Box>
                    </ListItem>
                  );
                })
              : null
            : null}
        </List>
        {/* Notes: Pagination Here */}
        {!props.loading && props.pageTotal > 1 && (
          <Pagination
            className={classes.pagination}
            count={props.pageTotal}
            page={props.page}
            shape='rounded'
            color='primary'
            onChange={(e, num) => props.handlePageChange(num)}
          />
        )}
      </Container>
      <Modal open={props.addIncidentModal}>
        <React.Fragment>
          <Box className={classes.content}>
            <FormProvider {...form}>
              <IncidentAddForm />
            </FormProvider>
          </Box>
          <Box className={classes.footer}>
            {!props.loading && (
              <Button alternate fullWidth onClick={() => handleAddModal(false)}>
                Cancel
              </Button>
            )}
            <Button
              onClick={form.handleSubmit(props.onCreateIncident)}
              neutral={props.loading}
              endIcon={
                props.loading && <CircularProgress color='inherit' size={16} sx={{ mr: '4px' }} />
              }
              fullWidth>
              {props.loading ? 'Please wait' : 'Save'}
            </Button>
          </Box>
        </React.Fragment>
      </Modal>
      <Modal style={{padding: '0 0 24px 0'}} open={props.editIncidentModal}>
        <React.Fragment>
          <Box className={classes.content}>
            <FormProvider {...form}>
              <IncidentDetails {...(props as IncidentsManageProps)} />
            </FormProvider>
          </Box>
        </React.Fragment>
      </Modal>
    </React.Fragment>
  );
};

export default IncidentsManageView;
